<template>
  <div>
    <!-- On the process page the hero and the activity's header show these facts (US 9369 + 9371); only an open
         update task, the guide and the dialogs stay here -->
    <template v-if="hideHeader">
      <div v-if="!showOnTask && (getTask('update_basic_info') || getGuide('update_basic_info'))" class="mt-2" qid="process-task-row">
        <span class="cursor-pointer task-label-open mr-2" v-if="getTask('update_basic_info')">
          <UpdateSingleTaskModal
              :task="myTasks[0]"
              @reloadContent="reloadAll"
              :editIcon="true"
          ></UpdateSingleTaskModal>
        </span>
        <span v-if="getGuide('update_basic_info')">
          <GuideButton :guide="getGuide('update_basic_info')"></GuideButton>
        </span>
      </div>
      <span v-if="isGranted([permissions.PROCESS_EDIT, permissions.PROCESS_EDIT_OWN], process)" class="pm-hidden-trigger">
        <ProcessEditForm ref="editForm"
            :object="process"
            :edit="edit"
            @reloadContent="reloadContent"
        ></ProcessEditForm>
      </span>
    </template>

    <!-- Basic info block -->
    <div v-else class="card" id="update_basic_info">
      <div class="card-body">
        <div class="row">
          <div class="col-sm-7">
            <h4 qid="p-view-page-title" class="mb-2">
              {{ process.name }}
            </h4>
            <div class="mb-2" v-if="!showOnTask">
              <span class="cursor-pointer task-label-open mr-2"
                    v-if="getTask('update_basic_info')"
              >
                <UpdateSingleTaskModal
                    :task="myTasks[0]"
                    @reloadContent="reloadAll"
                    :editIcon="true"
                ></UpdateSingleTaskModal>
              </span>
              <span v-if="getGuide('update_basic_info')">
                <GuideButton :guide="getGuide('update_basic_info')"></GuideButton>
              </span>
            </div>
            <span v-if="process.users && process.users.length" class="mr-2" qid="p-owners-list">
              {{ $t('table.owners') }}:
            </span>
            <span class="mr-1" v-for="user in process.users">
              <Avatar :object="user" size="xs"></Avatar>
            </span>
            <span v-if="process.tags_translated && process.tags_translated.length" class="mr-2 ml-2">
              {{ $t('table.tags') }}:
            </span>
            <span v-if="process.tags_translated && process.tags_translated.length" qid="activity-tags-list">
              <span class="badge" v-for="tag in getReducedTags(process.tags_translated)" :title="tag.name_translated">
                <font-awesome-icon icon="tag"/>
                {{ tag.name_translated | truncate(35) }}
              </span>
              <span v-if="process.tags_translated.length > 3"
                    class="badge cursor-pointer"
                    @click="openTagsModal(process)"
              >
                <font-awesome-icon icon="tags"/>
                + {{ process.tags_translated.length - 3 }}
              </span>
            </span>
            <span v-if="process.process_group" class="mr-2 ml-2">
              {{ $t('table.process_group') }}:
            </span>
            <span class="mr-1" qid="p-process-group" v-if="process.process_group">
              <span class="badge">
                <font-awesome-icon icon="project-diagram"/>
                {{ process.process_group ? process.process_group.name : '' }}
              </span>
            </span>
          </div>
          <div class="col-md-5 text-right" qid="view-p-modified">
              <div class="mb-4">
                <span v-if="isGranted([permissions.PROCESS_EDIT, permissions.PROCESS_EDIT_OWN], process)" class="h6 ml-3">
                  <ProcessEditForm
                      :object="process"
                      :edit="edit"
                      @reloadContent="reloadContent"
                  ></ProcessEditForm>
                  <span v-if="!showOnTask && isGranted([permissions.PROCESS_DELETE, permissions.PROCESS_DELETE_OWN], process)"
                        class="btn btn-delete"
                        qid="delete-p-button"
                        @click="showConfirmDeleteModal(process)"
                  >
                    <font-awesome-icon icon="trash-alt"/>
                  </span>
                </span>
              </div>
              <div>
                <span>{{ $t('table.last_updated') }}</span>
                    <span class="ml-2 text-muted small" qid="view-p-modified-date">
                  {{ process.updated_at | formatDate }}
                </span>
                <Avatar v-if="process.updated_by" :object="process.updated_by" size="xs" class="ml-3 mr-2"></Avatar>
                    <span v-if="process.updated_by" qid="view-p-modified-by">
                  {{ process.updated_by.name }}
                </span>
              </div>
          </div>
        </div>
      </div>
    </div>


    <!-- Delete Process modal template -->
    <b-modal ref="delete-process" hide-footer>
      <div class="d-block text-center">
        <h4 qid="delete-p-title">
          {{ $t('processes.delete.title') }}
        </h4>
      </div>
      <h5 class="text-center">
        {{ $t('processes.delete.description') }} {{ itemToDelete.name }}
      </h5>
      <h5 class="text-center" v-if="!blockedActivities.length">
        {{ $t('processes.delete.are_you_sure') }}
      </h5>
      <!-- Activities for which this is the only process block the delete (US 9369) -->
      <b-alert v-if="blockedActivities.length" show variant="warning" class="mt-3" qid="delete-p-blocked">
        {{ $t('processing_map.errors.process_has_sole_activities') }}
        <ul class="mb-0 mt-2">
          <li v-for="activity in blockedActivities" :key="activity.slug">
            <router-link :to="{ name: 'activities.view', params: { slug: activity.slug }}">{{ activity.name }}</router-link>
          </li>
        </ul>
      </b-alert>
      <div class="text-center">
        <b-button class="mt-3"
                  variant="secondary"
                  @click="hideConfirmDeleteModal"
                  qid="delete-p-cancel-button"
        >
          {{ $t('processes.delete.cancel') }}
        </b-button>
        <b-button @click="confirmDeleteTP(itemToDelete)"
                  class="mt-3 ml-3"
                  variant="success"
                  qid="delete-p-submit-button"
                  v-if="!blockedActivities.length && isGranted([permissions.PROCESS_DELETE, permissions.PROCESS_DELETE_OWN], process)"
        >
          {{ $t('processes.delete.submit') }}
        </b-button>
      </div>
    </b-modal>

    <ShowMoreModal
        :items="selectedItem.tags_translated"
        :show="showTagsModal"
        @closeViewModal="closeTagsModal"
        type="tags"
    ></ShowMoreModal>


  </div>
</template>

<script>
import piincHttp from '@/util/configureAxios.js'
import {config} from '@/config.js'
import ProcessEditForm from '@/views/Processes/ProcessEditForm.vue';
import Avatar from '@/components/Avatar.vue';
import ShowMoreModal from '@/components/ShowMoreModal.vue';
import UpdateSingleTaskModal from '@/views/Tasks/UpdateSingleTaskModal.vue';
import GuideButton from '@/components/guide/GuideButton.vue';


export default {
  name: 'BasicInfoBlock',
  data: function() {
    return {
      blockedActivities: [],
      breadcrumbItems: [
        {
          text: this.$t('dashboard.title'),
          to: '/'
        },
        {
          text: this.$t('processes.title'),
          to: '/processing-activities'
        }
      ],
      descriptionLength: config.descriptionLength,
      showFullDescription: false,
      showFullPurpose: false,
      process: {},
      selectedSort: '',
      selectedDirection: '',
      lbTableItems: [],
      lbLoading: true,
      lbValidationShow: false,

      itemToDelete: {},
      processGroups: [],
      tags: [],
      users: [],
      error: '',
      lbForm: {
        name: '',
        article_reason: '',
        links: []
      },
      inputLink: '',
      selectedArticle: [],
      articles: [],
      selectedFiles: [],
      showArticleDescription: false,
      selectedLB: {},
      viewLBModal: false,
      editLBModal: false,
      updateValidationList: false,
      selectedLBSort: '',
      selectedLBDirection: '',
      showDescriptionInput: false,
      showPurposeInput: false,
      processEditDescription: '',
      processEditPurpose: '',
      showActivitiesTab: false,
      showAssessmentsTab: false,
      showHistoryTab: false,
      showRiskTab: false,
      showGdprTab: false,
      buttonDisabled: false,
      myTasks: [],
      myGuides: [],
      tabIndex: 0,
      selectedItem: {},
      showTagsModal: false,
    }
  },
  props: {
    object: {
      type: Object,
      required: true
    },
    showOnTask: {},
    edit: {},
    hideHeader: { type: Boolean, default: false }
  },
  computed: {
    getClient() {
      return this.$store.state.client
    },
    getReloadContent() {
      return this.$store.state.updateContent
    },
    getReloadTasks() {
      return this.$store.state.reloadTasks
    }
  },
  watch: {
    object: function (value) {
      if (value && value.slug) {
        this.process = value
        this.loadTasks('update_basic_info');
      }
    },
    getReloadContent: function () {
      if (!this.showOnTask) {
        if (this.$store.state.updatedObject && this.$store.state.updatedObject.id) {
          if (this.$store.state.updatedObject.id == this.process.id && this.$store.state.updatedObject.who_am_i == this.process.who_am_i) {
            this.process = this.$store.state.updatedObject
          }
        }
      }
    },
    getReloadTasks: function () {
      if (!this.showOnTask) {
        this.loadTasks('update_basic_info')
      }
    },
  },
  components: {
    ProcessEditForm,
    Avatar,
    ShowMoreModal,
    UpdateSingleTaskModal,
    GuideButton
  },
  methods: {
    // For the hero's and the activity header's Edit and Delete (US 9369 + 9371)
    openEdit() {
      this.$refs.editForm && this.$refs.editForm.showEditPModal(this.process);
    },
    openDelete() {
      this.showConfirmDeleteModal(this.process);
    },
    goToList() {
      this.$router.push('/processing-activities')
    },
    reloadAll() {
      this.loadContent()
      this.loadTasks('update_basic_info');
    },
    openTagsModal(object) {
      this.selectedItem = object
      this.showTagsModal = true
    },
    closeTagsModal() {
      this.selectedItem = {}
      this.showTagsModal = false
    },
    getReducedTags(tags) {
      let reducedTags = []
      if (tags.length > 3) {
        reducedTags = _.cloneDeep(tags);
        reducedTags = reducedTags.slice(0, 3);
      } else {
        reducedTags = tags;
      }
      return reducedTags;
    },
    getTask(code) {
      let existingTask = _.find(this.myTasks, function(object) {
        return object.code == code
      });
      return existingTask
    },
    openToolbar(object) {
      this.$store.commit('storeTaskToolbarState', true);
      this.$store.commit('storeActiveTask', object);
    },
    getGuide(code) {
      let existingGuide = _.find(this.myGuides, function(object) {
        return object.scope == code && object.type == 'Process' && object.enabled
      });
      return existingGuide
    },
    loadGuides(code) {
      let self = this;
      let language = localStorage.getItem('lang')? localStorage.getItem('lang') : 'en';

      piincHttp.get('guides', { params:
        {
          per_page: 1,
          language: 'en',
          type: 'Process',
          scope: code,
          client: this.getClient.slug
        }
      }).then(function(response) {
        if (response.data.items.length) {
          self.myGuides.push(response.data.items[0])
        }
      }, function() {});
    },
    reloadContent(object) {
      this.loadTasks('update_basic_info');
      // If slug is not changed, just reload content
      if (object.slug == this.process.slug) {
        // Remove last item in breadcrumbs, because it will be added again on load content
        this.breadcrumbItems.pop();
        this.loadContent(true);
        this.$emit('updated', object);
      } else {
        this.$store.commit('storeUpdateTasks', true);
        // If slug changed, reload state with new param
        this.$router.push({ name: 'processes.view', params: { slug: object.slug }})
      }
    },
    updateValidation(validation) {
      // Event emmited from validation component, update active validation
      this.process.validation = validation
    },
    showConfirmDeleteModal(object) {
      this.itemToDelete = object
      this.blockedActivities = []
      this.$refs['delete-process'].show()
    },
    hideConfirmDeleteModal() {
      this.itemToDelete = {}
      this.blockedActivities = []
      this.$refs['delete-process'].hide()
    },
    confirmDeleteTP(object) {
      let self = this;
      piincHttp.delete('processes/' + object.slug).then(function() {
        self.hideConfirmDeleteModal();
        self.goToList();
        self.$store.commit('storeUpdateTasks', true);
        self.$store.commit('storeActiveTask', {});
        // Trigger toastr message after modal close
        self.$toastr('success', self.$t('processes.delete.success'))
      }, function(error) {
        if (error.status === 422 && error.data && error.data.activities) {
          self.blockedActivities = error.data.activities
        }
      });
    },
    loadContent(breadcrumbPush) {
      let self = this;
      piincHttp.get('processes/' + this.process.slug, { params:
        {
          client: this.getClient.slug
        }
      }).then(function(response) {
        self.process = response.data;
        if (breadcrumbPush) {
          // Push Third party name in breadcrumb array
          self.breadcrumbItems.push({text: self.process.name, active: true});
        }
      }, function() {});
    },
    loadTasks(code) {
      let self = this;
      if (this.getClient.projectModule) {
        piincHttp.get('projects/me/tasks', { params:
          {
            per_page: 1,
            status: 'pending',
            model_type: this.process.who_am_i,
            model_id: this.process.id,
            code: code,
            client: this.getClient.slug
          }
        }).then(function(response) {
          self.myTasks = []
          if (response.data.items.length) {
            self.myTasks.push(response.data.items[0])
          }
        }, function() {});
      }
    }
  },
  created () {
    this.process = this.object;
    if (this.getClient && this.getClient.slug) {

    }

    if (this.process && this.process.id) {
      this.loadTasks('update_basic_info');
    }

    this.loadGuides('update_basic_info');

  }
}
</script>
